import { podoFetcher } from '@server/infrastructure/external-apis/podo/client'
import { rpc } from '@server/infrastructure/rpc'
import {
  curriculumTypeSchema,
  GetRawSubscribeTicketsResponse,
  GetSubscribeTicketsReqSchema,
  langTypeSchema,
  paymentTypeSchema,
  PromotionType,
  SubscribeTicket,
  subscribeTicketAdapter,
} from '@server/schemas'
import { queryOptions } from '@tanstack/react-query'

import { getCachedTickets } from './subscribe.api.cache'

type BaseQuery = Omit<GetSubscribeTicketsReqSchema, 'paymentType'>
type SubscribeTicketDetailRequest = {
  ticketId: string
  promotionType?: PromotionType
  productId?: string | null
  purchaseOrderId?: string | null
  bearerToken?: string
}

export const subscribesTicketEntityQueries = {
  all: ['subscribesTickets'],

  // queryKeys
  getSubscribesTicketsQueryKey: (requestDto: RequestQueryOptionalOptions<SubscribeTicketDetailRequest>) => [
    subscribesTicketEntityQueries.all,
    requestDto,
  ],
  subscribeListQueryKey: (requestDto: RequestQueryOptionalOptions<Omit<BaseQuery, 'curriculumType'>>) => [
    subscribesTicketEntityQueries.all,
    'SUBSCRIBE',
    requestDto,
  ],
  lumpSumListQueryKey: (requestDto: RequestQueryOptionalOptions<Omit<BaseQuery, 'curriculumType'>>) => [
    subscribesTicketEntityQueries.all,
    'LUMP_SUM',
    requestDto,
  ],
  businessListQueryKey: (
    requestDto: RequestQueryOptionalOptions<
      Omit<BaseQuery, 'lessonMonth' | 'langType' | 'curriculumType' | 'lessonTime'>
    >,
  ) => [subscribesTicketEntityQueries.all, 'BUSINESS', requestDto],
  ipadListQueryKey: ({
    langType,
    lessonTime,
    bearerToken,
  }: RequestQueryOptionalOptions<Omit<BaseQuery, 'lessonCountPerMonth' | 'curriculumType'>>) => [
    subscribesTicketEntityQueries.all,
    'IPAD',
    bearerToken,
    langType,
    lessonTime,
  ],
  smartTalkListQueryKey: ({
    langType,
    lessonTime,
    bearerToken,
  }: RequestQueryOptionalOptions<Omit<BaseQuery, 'lessonCountPerMonth' | 'curriculumType'>>) => [
    subscribesTicketEntityQueries.all,
    'SMART_TALK',
    bearerToken,
    langType,
    lessonTime,
  ],

  // queryOptions
  getSubscribesTicket: (requestDto: RequestQueryOptionalOptions<SubscribeTicketDetailRequest>) =>
    queryOptions({
      queryKey: subscribesTicketEntityQueries.getSubscribesTicketsQueryKey(requestDto),
      queryFn: async () => {
        const query: { promotionType?: PromotionType; productId?: string; purchaseOrderId?: string } = {}

        if (requestDto.promotionType) {
          query.promotionType = requestDto.promotionType
        }
        if (requestDto.purchaseOrderId) {
          query.purchaseOrderId = requestDto.purchaseOrderId
        }
        if (requestDto.productId) {
          query.productId = requestDto.productId
        }

        const response = await rpc.api.v1.subscribes.tickets[':ticketId'].$get(
          {
            param: { ticketId: requestDto.ticketId },
            query,
          },
          requestDto.bearerToken
            ? {
                headers: {
                  Authorization: `Bearer ${requestDto.bearerToken}`,
                },
              }
            : undefined,
        )

        if (!response.ok) {
          throw new Error(`Failed to fetch subscribes ticket: ${response.status}`)
        }

        return response.json()
      },
    }),

  subscribeList: (requestDto: RequestQueryOptionalOptions<Omit<BaseQuery, 'curriculumType'>>) =>
    queryOptions({
      queryKey: subscribesTicketEntityQueries.subscribeListQueryKey(requestDto),
      queryFn: async () =>
        await getCachedTickets({
          paymentType: paymentTypeSchema.enum.SUBSCRIBE,
          curriculumType: curriculumTypeSchema.enum.BASIC,
          langType: requestDto.langType,
          lessonTime: requestDto.lessonTime,
          lessonCountPerMonth: requestDto.lessonCountPerMonth,
          promotionType: requestDto.promotionType,
        }),
    }),
  lumpSumList: (requestDto: RequestQueryOptionalOptions<Omit<BaseQuery, 'curriculumType'>>) =>
    queryOptions({
      queryKey: subscribesTicketEntityQueries.lumpSumListQueryKey(requestDto),
      queryFn: async () =>
        await getCachedTickets({
          paymentType: paymentTypeSchema.enum.LUMP_SUM,
          curriculumType: curriculumTypeSchema.enum.BASIC,
          langType: requestDto.langType,
          lessonTime: requestDto.lessonTime,
          promotionType: requestDto.promotionType,
          lessonCountPerMonth: requestDto.lessonCountPerMonth,
        }),
    }),

  businessList: (
    requestDto: RequestQueryOptionalOptions<
      Omit<BaseQuery, 'lessonMonth' | 'langType' | 'curriculumType' | 'lessonTime'>
    >,
  ) =>
    queryOptions({
      queryKey: subscribesTicketEntityQueries.businessListQueryKey(requestDto),
      queryFn: async () =>
        getCachedTickets({
          paymentType: paymentTypeSchema.enum.LUMP_SUM,
          curriculumType: curriculumTypeSchema.enum.BUSINESS,
          lessonCountPerMonth: requestDto.lessonCountPerMonth,
          langType: langTypeSchema.enum.EN,
          lessonTime: '25',
        }),
    }),

  ipadList: ({
    langType,
    lessonTime,
    bearerToken,
  }: RequestQueryOptionalOptions<Omit<BaseQuery, 'lessonCountPerMonth' | 'curriculumType'>>) =>
    queryOptions({
      queryKey: subscribesTicketEntityQueries.ipadListQueryKey({ langType, lessonTime, bearerToken }),
      queryFn: async () =>
        await getCachedTickets({
          paymentType: paymentTypeSchema.enum.IPAD,
          curriculumType: curriculumTypeSchema.enum.BASIC,
          langType,
          lessonTime,
        }),
    }),

  smartTalkList: ({
    langType,
    lessonTime,
    bearerToken,
  }: RequestQueryOptionalOptions<Omit<BaseQuery, 'lessonCountPerMonth' | 'curriculumType'>>) =>
    queryOptions({
      queryKey: subscribesTicketEntityQueries.smartTalkListQueryKey({ langType, lessonTime, bearerToken }),
      queryFn: async () =>
        await getCachedTickets({
          paymentType: paymentTypeSchema.enum.LUMP_SUM,
          curriculumType: curriculumTypeSchema.enum.SMART_TALK,
          langType,
          lessonTime,
        }),
    }),
  smartTalkTrialList: ({ userId }: { userId?: number } = {}) => ({
    queryKey: ['smartTalkTrial', 'KR_PRODUCT_V1', { userId }],
    queryFn: async (): Promise<SubscribeTicket[]> => {
      const search = new URLSearchParams({ catalogProtocol: 'KR_PRODUCT_V1' })
      if (userId) {
        search.set('studentId', String(userId))
      }
      const response = await podoFetcher<GetRawSubscribeTicketsResponse>({
        method: 'GET',
        url: `/api/v1/subscribe/meta/podo/trial/smartTalk?${search}`,
      })
      return response.data.map(subscribeTicketAdapter)
    },
  }),
} as const

export const SubscribesTicketEntityQueries = subscribesTicketEntityQueries
